import { getApi } from "./resonanceApi";

export type VideoMetaFields = {
  title?: string;
  description?: string;
  tags?: string; // comma-separated from backend
  category?: string;
  linked_uid?: string;
  linked_url?: string;
  name?: string;
  [key: string]: any;
};

export type VideoMeta = {
  uid: string;
  hlsUrl: string;
  thumbnailUrl: string;
  duration?: number | null;
  created?: string | null;
  meta?: VideoMetaFields;
};

export type VideosListResponse = {
  items: VideoMeta[];
  nextCursor: string | null;
};

export type VideoContextResponse = {
  current: VideoMeta;
  items: VideoMeta[];
  index: number;
  total: number;
  nextCursor: string | null;
  direction?: "next" | "prev";
};

export async function getVideos(params?: {
  tags?: string[];
  search?: string;
  limit?: number;
  cursor?: string;
}) {
  const sp = new URLSearchParams();

  if (params?.tags?.length) {
    sp.set("tags", params.tags.join(","));
  }

  if (params?.search?.trim()) {
    sp.set("search", params.search.trim());
  }

  if (params?.limit) {
    sp.set("limit", String(params.limit));
  }

  if (params?.cursor) {
    sp.set("cursor", params.cursor);
  }

  const queryString = sp.toString();
  const url = queryString ? `videos?${queryString}` : "videos";

  const { data } = await getApi<VideosListResponse>(url);
  return data;
}

export async function getVideo(id: string) {
  const { data } = await getApi<VideoMeta>(`videos/${encodeURIComponent(id)}`);
  return data;
}

/**
 * Fetch a page of videos relative to an anchor `id`.
 * - direction "next" (default): items after `id`
 * - direction "prev": items before `id` (returned in scroll-ready order)
 */
export async function getVideoContext(
  id: string,
  params?: {
    tags?: string[];
    search?: string;
    limit?: number;
    direction?: "next" | "prev";
  }
) {
  const sp = new URLSearchParams({ current: id });

  if (params?.tags?.length) {
    sp.set("tags", params.tags.join(","));
  }

  if (params?.search?.trim()) {
    sp.set("search", params.search.trim());
  }

  if (params?.limit) {
    sp.set("limit", String(params.limit));
  }

  if (params?.direction) {
    sp.set("direction", params.direction);
  }

  const { data } = await getApi<VideoContextResponse>(
    `videos/context?${sp.toString()}`
  );
  return data;
}

export const getNextFrom = (
  id: string,
  p?: { tags?: string[]; search?: string; limit?: number }
) => getVideoContext(id, { ...p, direction: "next" });

export const getPrevFrom = (
  id: string,
  p?: { tags?: string[]; search?: string; limit?: number }
) => getVideoContext(id, { ...p, direction: "prev" });